/**
 * Manual Transaction Form Component
 * 
 * Form for manually creating a single transaction
 */

import React, { useState, useEffect, useMemo } from 'react';
import { ChevronDown, X, Search } from 'lucide-react';
import { useCreateTransaction } from '../../hooks/useTransactions';
import { categoryService } from '../../services/category-service';
import type { Account } from '../../types/account.types';
import { AccountType } from '../../types/account.types';
import type { Category } from '../../types/category.types';
import { CreateTransactionSchema } from '../../utils/transaction-validation';
import { format } from 'date-fns';
import clsx from 'clsx';

// Helper function to get user-friendly account type label (reserved for future use)
 
export function getAccountTypeLabel(type: AccountType): string {
  const typeLabels: Record<AccountType, string> = {
    [AccountType.BANK_CASH]: 'Everyday',
    [AccountType.SAVINGS]: 'Savings',
    [AccountType.CREDIT_CARD]: 'Liability',
    [AccountType.LOAN]: 'Liability',
    [AccountType.INVESTMENTS_SUPERANNUATION]: 'Investment',
    [AccountType.ASSETS]: 'Asset'
  };
  return typeLabels[type] || 'Account';
}

interface ManualTransactionFormProps {
  accountId?: string;
  accounts: Account[];
  onComplete: () => void;
  onCancel: () => void;
}

export function ManualTransactionForm({
  accountId,
  accounts,
  onComplete,
  onCancel
}: ManualTransactionFormProps): React.ReactElement {
  const [selectedAccountId, setSelectedAccountId] = useState<string>(accountId || '');
  const [date, setDate] = useState<string>(format(new Date(), 'yyyy-MM-dd'));
  const [type, setType] = useState<'debit' | 'credit' | 'transfer'>('debit');
  const [amount, setAmount] = useState<string>('');
  const [description, setDescription] = useState<string>('');
  const [categoryId, setCategoryId] = useState<string>('');
  const [merchantName, setMerchantName] = useState<string>('');
  const [notes, setNotes] = useState<string>('');
  const [referenceNumber, setReferenceNumber] = useState<string>('');
  const [sourceAccountId, setSourceAccountId] = useState<string>('');
  const [destinationAccountId, setDestinationAccountId] = useState<string>('');

  const [categories, setCategories] = useState<Category[]>([]);
  const [loadingCategories, setLoadingCategories] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [success, setSuccess] = useState(false);
  
  // Account dropdown states
  const [isAccountDropdownOpen, setIsAccountDropdownOpen] = useState(false);
  const [isSourceAccountDropdownOpen, setIsSourceAccountDropdownOpen] = useState(false);
  const [isDestinationAccountDropdownOpen, setIsDestinationAccountDropdownOpen] = useState(false);
  const [accountSearchQuery, setAccountSearchQuery] = useState('');
  const [sourceAccountSearchQuery, setSourceAccountSearchQuery] = useState('');
  const [destinationAccountSearchQuery, setDestinationAccountSearchQuery] = useState('');

  const createTransaction = useCreateTransaction();

  // Sync selectedAccountId with accountId prop
  useEffect(() => {
    if (accountId) {
      setSelectedAccountId(accountId);
    }
  }, [accountId]);

  // Load categories
  useEffect(() => {
    const loadCategories = async () => {
      setLoadingCategories(true);
      try {
        const result = await categoryService.listCategories({ includeSystem: true });
        if (result.data) {
          setCategories(result.data.categories || []);
        }
      } catch (error) {
        console.error('Failed to load categories:', error);
      } finally {
        setLoadingCategories(false);
      }
    };
    loadCategories();
  }, []);

  // Filter categories based on transaction type
  const availableCategories = useMemo(() => {
    if (type === 'transfer') {
      // For transfers, show transfer categories
      return categories.filter(cat => cat.type === 'transfer');
    } else if (type === 'debit') {
      // For debits, show expense categories
      return categories.filter(cat => cat.type === 'expense');
    } else {
      // For credits, show all categories (income, expense, transfer)
      return categories;
    }
  }, [categories, type]);

  // Organize categories hierarchically
  const organizedCategories = useMemo(() => {
    const parentMap = new Map<string, Category & { children: Category[] }>();
    const allCategoriesMap = new Map<string, Category>();

    // First pass: create map and identify parents
    availableCategories.forEach(cat => {
      allCategoriesMap.set(cat.id, cat);
      if (!cat.parentId) {
        parentMap.set(cat.id, { ...cat, children: [] });
      }
    });

    // Second pass: add children to parents
    availableCategories.forEach(cat => {
      if (cat.parentId) {
        const parent = parentMap.get(cat.parentId);
        if (parent) {
          parent.children.push(cat);
        }
      }
    });

    // Sort children within each group
    parentMap.forEach(parent => {
      parent.children.sort((a, b) => a.name.localeCompare(b.name));
    });

    return Array.from(parentMap.values()).sort((a, b) =>
      a.name.localeCompare(b.name)
    );
  }, [availableCategories]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setErrors({});
    setSuccess(false);

    // Convert amount to number (handle negative values)
    const amountNum = parseFloat(amount);
    if (isNaN(amountNum) || amountNum === 0) {
      setErrors({ amount: 'Amount is required and must not be zero' });
      return;
    }

    // Validation schema requires positive amount, so convert to absolute value
    const absoluteAmount = Math.abs(amountNum);

    // Determine accountId based on type
    let finalAccountId = selectedAccountId;
    if (type === 'transfer') {
      // For transfers, use source account as the primary account
      finalAccountId = sourceAccountId;
    }

    const transactionData = {
      accountId: finalAccountId,
      amount: absoluteAmount,
      description: description.trim(),
      date: date,
      type: type,
      categoryId: categoryId || undefined,
      merchantName: merchantName.trim() || undefined,
      notes: notes.trim() || undefined,
      referenceNumber: referenceNumber.trim() || undefined,
      sourceAccountId: type === 'transfer' ? sourceAccountId : undefined,
      destinationAccountId: type === 'transfer' ? destinationAccountId : undefined,
    };

    // Validate using schema
    const validation = CreateTransactionSchema.safeParse(transactionData);
    if (!validation.success) {
      const fieldErrors: Record<string, string> = {};
      validation.error.issues.forEach((err) => {
        const path = err.path[0] as string;
        fieldErrors[path] = err.message;
      });
      setErrors(fieldErrors);
      return;
    }

    try {
      await createTransaction.mutateAsync(transactionData);
      setSuccess(true);
    } catch (error: any) {
      setErrors({ submit: error.message || 'Failed to create transaction' });
    }
  };

  const handleAddAnother = () => {
    // Reset form but keep account and type
    const currentAccount = selectedAccountId;
    const currentType = type;
    setDate(format(new Date(), 'yyyy-MM-dd'));
    setAmount('');
    setDescription('');
    setCategoryId('');
    setMerchantName('');
    setNotes('');
    setReferenceNumber('');
    setSourceAccountId('');
    setDestinationAccountId('');
    setErrors({});
    setSuccess(false);
    // Restore account and type
    setSelectedAccountId(currentAccount);
    setType(currentType);
  };

  // Group accounts by parent account type
  const getParentAccountType = (accountType: AccountType): string => {
    switch (accountType) {
      case AccountType.BANK_CASH:
        return 'Everyday';
      case AccountType.CREDIT_CARD:
      case AccountType.LOAN:
        return 'Liabilities';
      case AccountType.SAVINGS:
        return 'Savings';
      case AccountType.INVESTMENTS_SUPERANNUATION:
        return 'Investments';
      default:
        return 'Other';
    }
  };

  // Group all accounts by parent type
  const accountsByParentType = useMemo(() => {
    const filteredAccounts = accounts.filter((account) => account.type !== AccountType.ASSETS);
    const grouped = new Map<string, Account[]>();
    
    filteredAccounts.forEach(account => {
      const parentType = getParentAccountType(account.type);
      if (!grouped.has(parentType)) {
        grouped.set(parentType, []);
      }
      grouped.get(parentType)!.push(account);
    });

    // Sort accounts within each group by name
    grouped.forEach((accountList) => {
      accountList.sort((a, b) => a.name.localeCompare(b.name));
    });

    // Sort groups in a specific order
    const groupOrder = ['Everyday', 'Liabilities', 'Savings', 'Investments', 'Other'];
    const sortedGroups = new Map<string, Account[]>();
    groupOrder.forEach(type => {
      if (grouped.has(type)) {
        sortedGroups.set(type, grouped.get(type)!);
      }
    });

    return sortedGroups;
  }, [accounts]);

  // Filter accounts based on search query
  const getFilteredAccounts = (searchQuery: string, excludeAccountId?: string) => {
    if (!searchQuery.trim()) {
      const filtered = new Map<string, Account[]>();
      accountsByParentType.forEach((accountList, parentType) => {
        const filteredList = excludeAccountId 
          ? accountList.filter(acc => acc.id !== excludeAccountId)
          : accountList;
        if (filteredList.length > 0) {
          filtered.set(parentType, filteredList);
        }
      });
      return filtered;
    }

    const query = searchQuery.toLowerCase();
    const filtered = new Map<string, Account[]>();

    accountsByParentType.forEach((accountList, parentType) => {
      const matchingAccounts = accountList.filter(account => {
        if (excludeAccountId && account.id === excludeAccountId) return false;
        return (
          account.name.toLowerCase().includes(query) ||
          account.provider.toLowerCase().includes(query) ||
          parentType.toLowerCase().includes(query)
        );
      });
      
      if (matchingAccounts.length > 0) {
        filtered.set(parentType, matchingAccounts);
      }
    });

    return filtered;
  };

  // Filter accounts for transfer dropdowns (exclude selected account)
  const availableSourceAccounts = useMemo(() => {
    return accounts.filter(acc => acc.id !== destinationAccountId && acc.type !== AccountType.ASSETS);
  }, [accounts, destinationAccountId]);

  const availableDestinationAccounts = useMemo(() => {
    return accounts.filter(acc => acc.id !== sourceAccountId && acc.type !== AccountType.ASSETS);
  }, [accounts, sourceAccountId]);

  // Get selected account name
  const getSelectedAccountName = (accountId: string, accountsList: Account[]) => {
    if (!accountId) return '-- Select Account --';
    const account = accountsList.find(acc => acc.id === accountId);
    return account?.name || '-- Select Account --';
  };

  // Account dropdown component
  const AccountDropdown = ({
    value,
    onChange,
    excludeAccountId,
    searchQuery,
    setSearchQuery,
    isOpen,
    setIsOpen,
    error,
    accountsList
  }: {
    value: string;
    onChange: (accountId: string) => void;
    excludeAccountId?: string;
    searchQuery: string;
    setSearchQuery: (query: string) => void;
    isOpen: boolean;
    setIsOpen: (open: boolean) => void;
    error?: string;
    accountsList: Account[];
  }) => {
    const filteredAccounts = getFilteredAccounts(searchQuery, excludeAccountId);
    const selectedAccountName = getSelectedAccountName(value, accountsList);

    return (
      <div className="relative">
        <button
          type="button"
          onClick={() => setIsOpen(!isOpen)}
          className={clsx(
            'flex items-center justify-between w-full px-4 py-2.5 border rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-primary-500 focus:border-primary-500 text-left',
            error ? 'border-red-300 dark:border-red-700' : 'border-gray-300 dark:border-gray-600',
            'hover:border-gray-400 dark:hover:border-gray-500'
          )}
          aria-label="Select account"
          aria-expanded={isOpen}
          aria-required="true"
        >
          <span className={clsx(
            'text-sm truncate',
            !value && 'text-gray-500 dark:text-gray-400'
          )}>
            {selectedAccountName}
          </span>
          <ChevronDown
            className={clsx(
              'h-4 w-4 ml-2 flex-shrink-0 transition-transform text-gray-400',
              isOpen && 'rotate-180'
            )}
            aria-hidden="true"
          />
        </button>

        {isOpen && (
          <>
            <div
              className="fixed inset-0 z-10"
              onClick={() => {
                setIsOpen(false);
                setSearchQuery('');
              }}
              aria-hidden="true"
            />
            <div className="absolute left-0 mt-2 w-full z-20 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-xl overflow-hidden">
              {/* Search Box */}
              <div className="p-3 border-b border-gray-200 dark:border-gray-700">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                  <input
                    type="text"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    placeholder="Search accounts..."
                    className="w-full pl-10 pr-10 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                    autoFocus
                  />
                  {searchQuery && (
                    <button
                      type="button"
                      onClick={() => setSearchQuery('')}
                      className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                      aria-label="Clear search"
                    >
                      <X className="h-4 w-4" />
                    </button>
                  )}
                </div>
              </div>

              {/* Account List */}
              <div className="max-h-96 overflow-y-auto">
                {filteredAccounts.size === 0 ? (
                  <div className="p-4 text-center text-sm text-gray-500 dark:text-gray-400">
                    No accounts found
                  </div>
                ) : (
                  Array.from(filteredAccounts.entries()).map(([parentType, accountList]) => (
                    <div key={parentType} className="border-t border-gray-100 dark:border-gray-700 first:border-t-0">
                      {/* Parent Type Header */}
                      <div className="px-4 py-2 bg-gray-50 dark:bg-gray-900/50 text-xs font-semibold text-gray-700 dark:text-gray-300 uppercase tracking-wider">
                        {parentType}
                      </div>

                      {/* Accounts in this type */}
                      {accountList.map((account) => {
                        const isSelected = value === account.id;
                        const last4 = account.accountNumber && account.accountNumber.length >= 4
                          ? account.accountNumber.slice(-4)
                          : '';
                        
                        return (
                          <button
                            key={account.id}
                            type="button"
                            onClick={() => {
                              onChange(account.id);
                              setIsOpen(false);
                              setSearchQuery('');
                            }}
                            className={clsx(
                              'w-full px-4 py-2.5 text-sm transition-colors text-left',
                              isSelected
                                ? 'bg-primary-100 dark:bg-primary-900/30 text-primary-700 dark:text-primary-300 font-medium'
                                : 'text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700'
                            )}
                          >
                            <div className="flex-1 min-w-0">
                              <div className="font-medium truncate">{account.name}</div>
                              {account.provider && (
                                <div className="text-xs text-gray-500 dark:text-gray-400 truncate">
                                  {account.provider}{last4 ? ` • ${last4}` : ''}
                                </div>
                              )}
                            </div>
                          </button>
                        );
                      })}
                    </div>
                  ))
                )}
              </div>
            </div>
          </>
        )}
      </div>
    );
  };

  return (
    <form onSubmit={handleSubmit} className="flex flex-col min-h-0 flex-1">
      <div className="flex-1 min-h-0 overflow-y-auto px-6 py-4 space-y-6">
      {errors.submit && (
        <div className="p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
          <p className="text-sm font-medium text-red-800 dark:text-red-200">
            {errors.submit}
          </p>
        </div>
      )}

      {/* Form Fields Container */}
      <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Left Column */}
          <div className="space-y-6">
            {/* Account Selection */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Select Account <span className="text-red-500">*</span>
              </label>
              <AccountDropdown
                value={selectedAccountId}
                onChange={setSelectedAccountId}
                searchQuery={accountSearchQuery}
                setSearchQuery={setAccountSearchQuery}
                isOpen={isAccountDropdownOpen}
                setIsOpen={setIsAccountDropdownOpen}
                error={errors.accountId}
                accountsList={accounts}
              />
              {errors.accountId && (
                <p className="mt-1 text-sm text-red-600 dark:text-red-400">{errors.accountId}</p>
              )}
              {accounts.length === 0 && (
                <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                  No accounts found. Please create an account first.
                </p>
              )}
            </div>

            {/* Date */}
            <div>
              <label htmlFor="date" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Date <span className="text-red-500">*</span>
              </label>
              <input
                type="date"
                id="date"
                value={date}
                onChange={(e) => setDate(e.target.value)}
                className={`w-full px-4 py-2 border rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-primary-500 ${
                  errors.date ? 'border-red-300 dark:border-red-700' : 'border-gray-300 dark:border-gray-600'
                }`}
                required
              />
              {errors.date && (
                <p className="mt-1 text-sm text-red-600 dark:text-red-400">{errors.date}</p>
              )}
            </div>

            {/* Type */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Type <span className="text-red-500">*</span>
              </label>
              <div className="flex gap-4">
                <label className="flex items-center">
                  <input
                    type="radio"
                    name="type"
                    value="debit"
                    checked={type === 'debit'}
                    onChange={(e) => {
                      setType(e.target.value as 'debit');
                      setCategoryId(''); // Reset category when type changes
                    }}
                    className="mr-2"
                  />
                  <span className="text-sm text-gray-700 dark:text-gray-300">Debit</span>
                </label>
                <label className="flex items-center">
                  <input
                    type="radio"
                    name="type"
                    value="credit"
                    checked={type === 'credit'}
                    onChange={(e) => {
                      setType(e.target.value as 'credit');
                      setCategoryId(''); // Reset category when type changes
                    }}
                    className="mr-2"
                  />
                  <span className="text-sm text-gray-700 dark:text-gray-300">Credit</span>
                </label>
                <label className="flex items-center">
                  <input
                    type="radio"
                    name="type"
                    value="transfer"
                    checked={type === 'transfer'}
                    onChange={(e) => {
                      setType(e.target.value as 'transfer');
                      setCategoryId(''); // Reset category when type changes
                    }}
                    className="mr-2"
                  />
                  <span className="text-sm text-gray-700 dark:text-gray-300">Transfer</span>
                </label>
              </div>
              {errors.type && (
                <p className="mt-1 text-sm text-red-600 dark:text-red-400">{errors.type}</p>
              )}
            </div>

            {/* Transfer Account Fields */}
            {type === 'transfer' && (
              <>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Source Account <span className="text-red-500">*</span>
                  </label>
                  <AccountDropdown
                    value={sourceAccountId}
                    onChange={setSourceAccountId}
                    excludeAccountId={destinationAccountId}
                    searchQuery={sourceAccountSearchQuery}
                    setSearchQuery={setSourceAccountSearchQuery}
                    isOpen={isSourceAccountDropdownOpen}
                    setIsOpen={setIsSourceAccountDropdownOpen}
                    error={errors.sourceAccountId}
                    accountsList={availableSourceAccounts}
                  />
                  {errors.sourceAccountId && (
                    <p className="mt-1 text-sm text-red-600 dark:text-red-400">{errors.sourceAccountId}</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Destination Account <span className="text-red-500">*</span>
                  </label>
                  <AccountDropdown
                    value={destinationAccountId}
                    onChange={setDestinationAccountId}
                    excludeAccountId={sourceAccountId}
                    searchQuery={destinationAccountSearchQuery}
                    setSearchQuery={setDestinationAccountSearchQuery}
                    isOpen={isDestinationAccountDropdownOpen}
                    setIsOpen={setIsDestinationAccountDropdownOpen}
                    error={errors.destinationAccountId}
                    accountsList={availableDestinationAccounts}
                  />
                  {errors.destinationAccountId && (
                    <p className="mt-1 text-sm text-red-600 dark:text-red-400">{errors.destinationAccountId}</p>
                  )}
                </div>
              </>
            )}

            {/* Amount */}
            <div>
              <label htmlFor="amount" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Amount <span className="text-red-500">*</span>
              </label>
              <input
                type="number"
                id="amount"
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                step="0.01"
                placeholder={type === 'debit' ? '-100.00' : '100.00'}
                className={`w-full px-4 py-2 border rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-primary-500 ${
                  errors.amount ? 'border-red-300 dark:border-red-700' : 'border-gray-300 dark:border-gray-600'
                }`}
                required
              />
              <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                {type === 'debit' ? 'Enter a negative value for debits (e.g., -100.00)' : 'Enter a positive value for credits (e.g., 100.00)'}
              </p>
              {errors.amount && (
                <p className="mt-1 text-sm text-red-600 dark:text-red-400">{errors.amount}</p>
              )}
            </div>
          </div>

          {/* Right Column */}
          <div className="space-y-6">
            {/* Description */}
            <div>
              <label htmlFor="description" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Description <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                id="description"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                className={`w-full px-4 py-2 border rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-primary-500 ${
                  errors.description ? 'border-red-300 dark:border-red-700' : 'border-gray-300 dark:border-gray-600'
                }`}
                required
              />
              {errors.description && (
                <p className="mt-1 text-sm text-red-600 dark:text-red-400">{errors.description}</p>
              )}
            </div>

            {/* Category */}
            <div>
              <label htmlFor="category" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Category
              </label>
              <select
                id="category"
                value={categoryId}
                onChange={(e) => setCategoryId(e.target.value)}
                disabled={loadingCategories}
                className={`w-full px-4 py-2 border rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-primary-500 ${
                  errors.categoryId ? 'border-red-300 dark:border-red-700' : 'border-gray-300 dark:border-gray-600'
                } ${loadingCategories ? 'opacity-60 cursor-not-allowed' : ''}`}
              >
                <option value="">No category</option>
                {organizedCategories.map(parent => (
                  <React.Fragment key={parent.id}>
                    <option value={parent.id}>
                      {parent.name}
                    </option>
                    {parent.children.map(child => (
                      <option key={child.id} value={child.id}>
                        {parent.name} &gt; {child.name}
                      </option>
                    ))}
                  </React.Fragment>
                ))}
              </select>
              {errors.categoryId && (
                <p className="mt-1 text-sm text-red-600 dark:text-red-400">{errors.categoryId}</p>
              )}
            </div>

            {/* Merchant Name */}
            <div>
              <label htmlFor="merchantName" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Merchant Name
              </label>
              <input
                type="text"
                id="merchantName"
                value={merchantName}
                onChange={(e) => setMerchantName(e.target.value)}
                className={`w-full px-4 py-2 border rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-primary-500 ${
                  errors.merchantName ? 'border-red-300 dark:border-red-700' : 'border-gray-300 dark:border-gray-600'
                }`}
              />
              {errors.merchantName && (
                <p className="mt-1 text-sm text-red-600 dark:text-red-400">{errors.merchantName}</p>
              )}
            </div>

            {/* Reference Number */}
            <div>
              <label htmlFor="referenceNumber" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Reference Number
              </label>
              <input
                type="text"
                id="referenceNumber"
                value={referenceNumber}
                onChange={(e) => setReferenceNumber(e.target.value)}
                className={`w-full px-4 py-2 border rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-primary-500 ${
                  errors.referenceNumber ? 'border-red-300 dark:border-red-700' : 'border-gray-300 dark:border-gray-600'
                }`}
              />
              {errors.referenceNumber && (
                <p className="mt-1 text-sm text-red-600 dark:text-red-400">{errors.referenceNumber}</p>
              )}
            </div>

            {/* Notes - Full width in right column */}
            <div className="md:col-span-1">
              <label htmlFor="notes" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Notes
              </label>
              <textarea
                id="notes"
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                rows={3}
                maxLength={500}
                className={`w-full px-4 py-2 border rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-primary-500 ${
                  errors.notes ? 'border-red-300 dark:border-red-700' : 'border-gray-300 dark:border-gray-600'
                }`}
              />
              <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                {notes.length}/500 characters
              </p>
              {errors.notes && (
                <p className="mt-1 text-sm text-red-600 dark:text-red-400">{errors.notes}</p>
              )}
            </div>
          </div>
        </div>
      </div>

      {success && (
        <div className="p-4 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg">
          <p className="text-sm font-medium text-green-800 dark:text-green-200">
            Transaction created successfully!
          </p>
        </div>
      )}
      </div>

      {/* Action Buttons — pinned below scrollable form fields */}
      <div className="flex-shrink-0 flex items-center justify-end gap-3 px-6 py-4 border-t border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800">
        <button
          type="button"
          onClick={onCancel}
          className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-primary-500"
        >
          Cancel
        </button>
        {success && (
          <button
            type="button"
            onClick={onComplete}
            className="px-4 py-2 text-sm font-medium text-white bg-primary-600 rounded-lg hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-primary-500"
          >
            Done
          </button>
        )}
        {success && (
          <button
            type="button"
            onClick={handleAddAnother}
            className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-primary-500"
          >
            Add Another
          </button>
        )}
        {!success && (
          <button
            type="submit"
            disabled={createTransaction.isPending}
            className="px-4 py-2 text-sm font-medium text-white bg-primary-600 rounded-lg hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-primary-500 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {createTransaction.isPending ? 'Creating...' : 'Create Transaction'}
          </button>
        )}
      </div>
    </form>
  );
}

